import { useEffect, useRef } from 'react'
import { chartTimeSeconds, TIMEFRAME_SECONDS } from '../../lib/chart/chartTimeProjection.js'
import { chartTimeLabelFormatter } from '../../lib/chart/chartTimeLabel.js'

export function futureHoverTime(chart, last, x, timeframe) {
  const scale = chart?.timeScale()
  if (!last || !scale) return null
  const logical = scale.coordinateToLogical(x)
  const lastIndex = scale.timeToIndex(last.time)
  if (!Number.isFinite(logical) || !Number.isFinite(lastIndex) || logical <= lastIndex + 0.5) return null
  const steps = Math.round(logical - lastIndex), seconds = chartTimeSeconds(last.time)
  if (seconds === null) return null
  if (timeframe === 'monthly') {
    const date = new Date(seconds * 1000)
    date.setUTCDate(1); date.setUTCMonth(date.getUTCMonth() + steps)
    return date.getTime() / 1000
  }
  return TIMEFRAME_SECONDS[timeframe] ? seconds + steps * TIMEFRAME_SECONDS[timeframe] : null
}

// Display-only projection. Never inserts future candles, volumes or indicator data.
export default function FutureTimeLabel({ chart, series, candlesRef, container, timeframe, enabled = true }) {
  const label = useRef(null)
  useEffect(() => {
    if (!chart || !series || !container || !enabled) return
    const node = label.current
    const hide = () => { if (node) node.hidden = true }
    const move = event => {
      const bounds = container.getBoundingClientRect(), scale = chart.timeScale()
      const x = event.clientX - bounds.left - (chart.priceScale('left').width() || 0)
      const y = event.clientY - bounds.top
      if (x < 0 || x > scale.width() || y < 0 || y >= bounds.height - scale.height()) return hide()
      const time = futureHoverTime(chart, candlesRef.current?.at(-1), x, timeframe)
      if (time === null) return hide()
      node.textContent = chartTimeLabelFormatter(timeframe)(time)
      node.hidden = false
      node.style.left = `${Math.max(0, Math.min(event.clientX - bounds.left - node.offsetWidth / 2, bounds.width - node.offsetWidth))}px`
    }
    container.addEventListener('pointermove', move)
    container.addEventListener('pointerleave', hide)
    return () => { container.removeEventListener('pointermove', move); container.removeEventListener('pointerleave', hide); hide() }
  }, [chart, series, candlesRef, container, timeframe, enabled])
  return <div ref={label} hidden data-testid="future-chart-time" title="Projected chart interval" className="pointer-events-none absolute bottom-0 z-20 rounded-sm bg-ink px-2 py-1 font-mono text-[11px] text-canvas" />
}
